import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import HomePage from './pages/HomePage';
import AccountSettings from './pages/AccountSettings';
import MemberProfile from './pages/MemberProfile';
import TimelineView from './pages/TimelineView';
import ObituariesPage from './pages/ObituariesPage';
import AdminPanel from './pages/AdminPanel';
import MemorialLandingPage from './pages/MemorialLandingPage';
import CreateMemorial from './pages/CreateMemorial';
import MemorialTributePage from './pages/MemorialTributePage';
import FamilyTreeLandingPage from './pages/FamilyTreeLandingPage';
import FamilyTreeBuilder from './pages/FamilyTreeBuilder';
import FamilyTreeView from './pages/FamilyTreeView';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ProtectedRoute from './components/ProtectedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppRouter({ user, setUser }) {
  const location = useLocation();
  
  // Check URL fragment for session_id - must be synchronous during render
  // Also check window.location.hash as a fallback for initial load
  const locationHash = location.hash || '';
  const windowHash = window.location.hash || '';
  const hasSessionId = locationHash.includes('session_id=') || windowHash.includes('session_id=');
  
  // Debug logging
  console.log('AppRouter render:', {
    path: location.pathname,
    locationHash,
    windowHash,
    hasSessionId
  });
  
  if (hasSessionId) {
    console.log('Rendering AuthCallback component');
    return <AuthCallback setUser={setUser} />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage user={user} />} />
      
      {/* Terms and Privacy (Public) */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Memorial Tribute Landing (Public) */}
      <Route path="/memorial-tribute" element={<MemorialLandingPage user={user} />} />
      
      {/* Family Tree Landing (Public) */}
      <Route path="/family-tree" element={<FamilyTreeLandingPage user={user} />} />
      
      {/* Public Memorial Pages */}
      <Route path="/tribute/:memorialId" element={<MemorialTributePage user={user} />} />
      
      {/* Protected Home Page */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      
      {/* Protected Memorial Creation */}
      <Route path="/create-memorial" element={
        <ProtectedRoute>
          <CreateMemorial />
        </ProtectedRoute>
      } />
      
      {/* Protected Family Tree Builder */}
      <Route path="/tree-builder" element={
        <ProtectedRoute>
          <FamilyTreeBuilder />
        </ProtectedRoute>
      } />
      
      <Route path="/tree-builder/:treeId" element={
        <ProtectedRoute>
          <FamilyTreeBuilder />
        </ProtectedRoute>
      } />
      
      {/* Public Tree View */}
      <Route path="/tree-view/:treeId" element={<FamilyTreeView />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/member/:memberId" element={
        <ProtectedRoute>
          <MemberProfile />
        </ProtectedRoute>
      } />
      
      <Route path="/timeline" element={
        <ProtectedRoute>
          <TimelineView />
        </ProtectedRoute>
      } />
      
      <Route path="/obituaries" element={
        <ProtectedRoute>
          <ObituariesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      
      {/* Account Settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <AccountSettings />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for session_id in hash BEFORE doing auth check
    // This handles the OAuth callback immediately
    if (window.location.hash?.includes('session_id=')) {
      console.log('Session ID detected in URL hash, skipping initial auth check');
      setLoading(false);
      return;
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppRouter user={user} setUser={setUser} />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
